<template>
  <div class="el-form-validation-margin">
    <label class="required form-label" for="numberOfTickets">{{
      t('subscribeToEventUpdatesModal.firstStep.form.numberOfTickets.label')
    }}</label>
    <div class="position-relative">
      <input
        id="numberOfTickets"
        ref="numberOfTicketsInput"
        v-model="localNumberOfTickets"
        :placeholder="
          t(
            'subscribeToEventUpdatesModal.firstStep.form.numberOfTickets.placeholder'
          )
        "
        autocomplete="numberOfTickets"
        class="w-full border border-gray-200 rounded-lg p-2 bg-white mt-1"
        name="numberOfTickets"
        min="1"
        type="number"
        @blur="commitNumberOfTickets"
      />
    </div>
    <span
      v-if="
        showTicketValidation &&
        numberOfTickets != null &&
        numberOfTickets < minTickets &&
        isFromEventPage &&
        eventGetters.isDateRequestActive(eventData)
      "
      class="text-red text-sm block"
    >
      {{
        t(
          'subscribeToEventUpdatesModal.firstStep.form.numberOfTickets.dateRequestError',
          { minTicketsDateRequest: minTickets }
        )
      }}
    </span>
    <span
      v-else-if="showTicketValidation && showPartySizePurchasePrivateGap"
      class="text-red text-sm block"
    >
      {{
        t('dateRequest.partySizePurchasePrivateGap', {
          purchaseMax: purchaseMaxTickets,
          privateMin: privateEventMinTickets,
        })
      }}
    </span>
    <span
      v-else-if="showTicketValidation && showPartySizeAboveDateRequestMax"
      class="text-red text-sm block"
    >
      {{
        t(
          'subscribeToEventUpdatesModal.firstStep.form.numberOfTickets.aboveDateRequestMaxError',
          { maxTickets: purchaseMaxTickets }
        )
      }}
    </span>
    <span
      v-if="invalidKeys.includes('numberOfTickets')"
      data-test-id="date-request-form-invalid"
      class="text-red text-sm block"
    >
      <span v-if="errors.numberOfTickets">
        {{ errors.numberOfTickets }}
      </span>
      <span
        v-else-if="
          numberOfTickets != null &&
          numberOfTickets < privateEventMinTickets &&
          privateEventMinTickets < minTickets
        "
      >
        {{
          t(
            'subscribeToEventUpdatesModal.firstStep.form.numberOfTickets.privateEventError',
            { privateEventMinTickets }
          )
        }}
      </span>
    </span>
    <span
      v-if="showTicketValidation && showOutOfScopeError"
      class="text-red text-sm block"
    >
      {{
        t(
          'subscribeToEventUpdatesModal.firstStep.form.numberOfTickets.errorText'
        )
      }}
    </span>
    <span
      v-if="
        !eventGetters.isPrivateEventRequestEnabled(eventData) &&
        !eventGetters.isDateRequestActive(eventData)
      "
      class="text-red text-sm block"
    >
      {{
        t(
          'subscribeToEventUpdatesModal.firstStep.form.numberOfTickets.requestDisabledErrorText'
        )
      }}
    </span>
    <span
      v-else-if="showTicketValidation && showEventInactiveHelper"
      class="text-red text-sm block"
    >
      {{
        t(
          'subscribeToEventUpdatesModal.firstStep.form.numberOfTickets.eventInactiveBelowPrivateMinError',
          { privateEventMinTickets }
        )
      }}
    </span>
  </div>
</template>

<script setup lang="ts">
import type {
  DateRequestErrors,
  DateRequestInvalidKeys,
} from '~/components/date-request/dateRequestTypes'
import type { EventDescription } from '~/composables/types/event'
import { eventGetters } from '~/utils/eventGetters'

interface Props {
  numberOfTickets: number | null
  privateEventMinTickets: number
  /** Max tickets from date-request settings (matching-dates purchase band). */
  purchaseMaxTickets: number
  /** Minimum tickets for date-request messaging (standard error branch). */
  minTickets: number
  invalidKeys: DateRequestInvalidKeys
  errors?: DateRequestErrors
  eventData?: EventDescription
  isFromEventPage?: boolean
  autoFocus?: boolean
  showOutOfScopeError?: boolean
  showEventInactiveHelper?: boolean
  showPartySizePurchasePrivateGap?: boolean
  showPartySizeAboveDateRequestMax?: boolean
  /** Inline ticket validation; off until the user commits a non-null count. */
  showTicketValidation?: boolean
}

const {
  numberOfTickets,
  privateEventMinTickets,
  purchaseMaxTickets,
  minTickets,
  invalidKeys,
  errors = {},
  isFromEventPage = false,
  autoFocus = false,
  showOutOfScopeError = false,
  showEventInactiveHelper = false,
  showPartySizePurchasePrivateGap = false,
  showPartySizeAboveDateRequestMax = false,
  showTicketValidation = false,
} = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:numberOfTickets', value: number | null): void
  (e: 'clear-errors', field: string): void
  (e: 'number-of-tickets-user-change', event: FocusEvent): void
}>()

const { t } = useI18n()

const numberOfTicketsInput = ref<HTMLInputElement | null>(null)

const localNumberOfTickets = ref<string | number>(numberOfTickets ?? '')
watch(
  () => numberOfTickets,
  (val) => {
    localNumberOfTickets.value = val ?? ''
  }
)

const commitNumberOfTickets = (event: FocusEvent) => {
  const raw = localNumberOfTickets.value
  let next: number | null = raw === '' || raw === null ? null : Number(raw)
  if (next !== null && next < 0) {
    next = 0
    localNumberOfTickets.value = 0
  }
  emit('update:numberOfTickets', next)
  emit('clear-errors', 'numberOfTickets')
  emit('number-of-tickets-user-change', event)
}

const { isMobile } = useDevice()

onMounted(() => {
  nextTick(() => {
    if (autoFocus && numberOfTicketsInput.value && !isMobile) {
      numberOfTicketsInput.value.focus()
    }
  })
})

watch(
  () => autoFocus,
  (shouldFocus) => {
    if (!shouldFocus) {
      return
    }
    nextTick(() => {
      if (numberOfTicketsInput.value && !isMobile) {
        numberOfTicketsInput.value.focus()
      }
    })
  }
)
</script>
